/** Fixed queue-race goals. Same five every UTC day, outside the rolled board. */

export const RUNNER_CLEAR_CAP = 300
export const RUNNER_STEP_CAP = 5000
/**
 * Largest score increase accepted between two clears.
 * The marker climbs about 12 steps a second, and a card gap stays under this.
 */
export const RUNNER_STEP_JUMP = 48

export const RUNNER_MISSION_IDS = [
  'runner-cards-5',
  'runner-cards-20',
  'runner-cards-40',
  'runner-steps-100',
  'runner-steps-400',
] as const

export type RunnerMissionId = (typeof RUNNER_MISSION_IDS)[number]

export type RunnerMissionRow = {
  id: RunnerMissionId
  progress: number
  target: number
  done: boolean
  claimed: boolean
}

const RUNNER_DEFS: readonly {
  id: RunnerMissionId
  kind: 'clears' | 'steps'
  target: number
}[] = [
  { id: 'runner-cards-5', kind: 'clears', target: 5 },
  { id: 'runner-cards-20', kind: 'clears', target: 20 },
  { id: 'runner-cards-40', kind: 'clears', target: 40 },
  { id: 'runner-steps-100', kind: 'steps', target: 100 },
  { id: 'runner-steps-400', kind: 'steps', target: 400 },
]

export function isRunnerMissionId(id: string): id is RunnerMissionId {
  return (RUNNER_MISSION_IDS as readonly string[]).includes(id)
}

/**
 * Keep the day's best score. A lower report does not move it.
 * A jump larger than one card gap only advances by that cap, so one
 * missed report does not freeze the best for the rest of the run.
 */
export function acceptRunnerStep(prevBest: number, reported: number): number {
  const prev = Number.isFinite(prevBest) ? Math.max(0, Math.trunc(prevBest)) : 0
  if (typeof reported !== 'number' || !Number.isFinite(reported)) return prev
  const score = Math.max(0, Math.trunc(reported))
  if (score <= prev) return prev
  const capped = score - prev > RUNNER_STEP_JUMP ? prev + RUNNER_STEP_JUMP : score
  return Math.min(RUNNER_STEP_CAP, capped)
}

export function parseRunnerClaimed(raw: unknown): RunnerMissionId[] {
  let value = raw
  if (typeof value === 'string') {
    try {
      value = JSON.parse(value) as unknown
    } catch {
      return []
    }
  }
  if (!Array.isArray(value)) return []
  const out: RunnerMissionId[] = []
  const seen = new Set<string>()
  for (const item of value) {
    if (typeof item !== 'string' || !isRunnerMissionId(item) || seen.has(item)) continue
    seen.add(item)
    out.push(item)
  }
  return out
}

export function runnerRows(
  clears: number,
  best: number,
  claimed: readonly string[],
): RunnerMissionRow[] {
  const safeClears = Number.isFinite(clears)
    ? Math.max(0, Math.min(RUNNER_CLEAR_CAP, Math.trunc(clears)))
    : 0
  const safeBest = Number.isFinite(best)
    ? Math.max(0, Math.min(RUNNER_STEP_CAP, Math.trunc(best)))
    : 0
  const claimedSet = new Set(claimed)
  return RUNNER_DEFS.map((def) => {
    const progress = def.kind === 'clears' ? safeClears : safeBest
    const done = progress >= def.target
    return {
      id: def.id,
      progress: Math.min(progress, def.target),
      target: def.target,
      done,
      claimed: done && claimedSet.has(def.id),
    }
  })
}
